const LocalStorageKeys = require('@/lib/Storage/LocalStorageKeys');
const User = require('@/lib/Users/User');
const { container } = require('@/container');

/**
 * Handles logging a user in to the application.
 * This is separate from UserService because the process involves doing things
 * with devices too, and trying to do it all in UserService causes circular dependencies.
 */
class LoginService {
    /**
     * @param {ApiClient} apiClient
     * @param {CurrentDevice} currentDevice
     * @param {Hydrator} hydrator
     * @param {LocalStorageDataStore} localStorageDataStore
     * @param {LogoService} logoService
     * @param {SyncedDataManager} syncedDataManager
     * @param {UserService} userService
     * @param {UserSessionManager} userSessionManager
     */
    constructor(
        apiClient,
        currentDevice,
        hydrator,
        localStorageDataStore,
        logoService,
        syncedDataManager,
        userService,
        userSessionManager
    ) {
        this.apiClient = apiClient;
        this.currentDevice = currentDevice;
        this.hydrator = hydrator;
        this.localStorageDataStore = localStorageDataStore;
        this.logoService = logoService;
        this.syncedDataManager = syncedDataManager;
        this.userService = userService;
        this.userSessionManager = userSessionManager;
    }

    async sendLoginRequest({ username, password }) {
        return container.apiClient
            .request(
                'POST',
                'users/auth',
                {
                    username,
                    password,
                    resolve: '*',
                    encodePrintableLogos: 1,
                },
            );
    }

    /**
     * @param {{}} responseData
     *
     * @return {Promise<User>}
     */
    async loginFromApiResponse(responseData) {
        // Extract any logos (base64 encoded images) from the response and store them
        // in the ImageRepository instead of in local storage.
        await this.logoService.extractLogosFromUserData(responseData.user);

        const user = new User();
        this.hydrator.hydrate(responseData.user, user);

        // Create or load an existing session for this user.
        const userSession = await this.userSessionManager.getOrCreateSession(user);

        // Ensure the values in the user session are up to date.
        // Even if we just created a session we want to set the lastLoginDatetime.
        userSession.apiKey = user.apiKey;
        userSession.lastLoginDatetime = new Date();
        userSession.username = user.username;
        await this.userSessionManager.persist(userSession);

        if (this.localStorageDataStore.find(LocalStorageKeys.LAST_ACTIVE_COMPANY_ID) !== user.company.ID) {
            // If the till user has switched companies then clear the synced data to prevent errors.
            this.currentDevice.clearDevice();
            await this.syncedDataManager.clearData();
        }
        // Store the companyID of the current user, so we check this at next login.
        this.localStorageDataStore.persist(LocalStorageKeys.LAST_ACTIVE_COMPANY_ID, user.company.ID);

        // Set the new current user.
        await this.userService.changeUser(user);

        return user;
    }
}

module.exports = LoginService;
